/** Persisting the assistant conversation across a page load.
 *
 *  The panel keeps the whole conversation in React state and the backend is deliberately
 *  stateless about it (the client re-sends `history` every turn), so before this module a
 *  reload — or the 30-minute inactivity logout — lost it outright. Minimising already kept
 *  the component mounted; this covers the other half.
 *
 *  Two things it deliberately does NOT do:
 *
 *  - It never restores a *pending* proposal as pending. The ghost preview lives in the map's
 *    state and is gone after a reload, so a live Confirm button would write a feature the
 *    operator cannot see. Those come back as `expired`; the operator asks again.
 *  - It is not a per-user store. The conversation can quote group data, so `AuthContext`
 *    clears it on both login and logout rather than this module trying to tell users apart.
 */
import type { ChatMessage } from './assistantTypes';

export const CHAT_STORAGE_KEY = 'toc_assistantChat';

/** Newest turns kept. The backend only ever sees the last MAX_HISTORY_TURNS anyway; the rest
 *  is scrollback for the operator. */
export const MAX_STORED_MESSAGES = 60;

/** Measured in JSON characters, which is also how browsers count the ~5 MB quota. A batch
 *  proposal carries every coordinate it drew, so a long research conversation is bulky. */
export const MAX_STORED_BYTES = 256 * 1024;

const VERSION = 1;

export interface RestoredChat {
  messages: ChatMessage[];
  conversationId: string | null;
}

function isChatMessage(value: unknown): value is ChatMessage {
  if (!value || typeof value !== 'object') return false;
  const { role, content } = value as { role?: unknown; content?: unknown };
  return (role === 'user' || role === 'assistant') && typeof content === 'string';
}

/** An offer that needed something the reload destroyed — the map ghost, the live catalogue —
 *  is shown as expired rather than actionable or falsely "discarded". */
function expireOffers(message: ChatMessage): ChatMessage {
  const restored = { ...message };
  if (restored.proposalState === 'pending') restored.proposalState = 'expired';
  if (restored.modelOfferState === 'pending') restored.modelOfferState = 'expired';
  return restored;
}

/** Serialise, dropping the oldest turns until it fits. `null` means store nothing. */
export function encodeChat(messages: ChatMessage[], conversationId: string | null): string | null {
  let kept = messages.slice(-MAX_STORED_MESSAGES);
  while (kept.length) {
    const raw = JSON.stringify({ v: VERSION, conversationId, messages: kept });
    if (raw.length <= MAX_STORED_BYTES) return raw;
    kept = kept.slice(1);
  }
  return null;
}

/** Parse what was stored. Anything unrecognised is discarded rather than repaired — a stale
 *  shape from an older build must not reach the panel's render. */
export function decodeChat(raw: string | null): RestoredChat | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const blob = parsed as { v?: unknown; conversationId?: unknown; messages?: unknown };
  if (blob.v !== VERSION || !Array.isArray(blob.messages)) return null;

  const messages = blob.messages.filter(isChatMessage).map(expireOffers);
  if (!messages.length) return null;

  return {
    messages,
    conversationId: typeof blob.conversationId === 'string' ? blob.conversationId : null,
  };
}

export function loadChat(): RestoredChat | null {
  try {
    return decodeChat(localStorage.getItem(CHAT_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function saveChat(messages: ChatMessage[], conversationId: string | null): void {
  const raw = encodeChat(messages, conversationId);
  try {
    if (raw) localStorage.setItem(CHAT_STORAGE_KEY, raw);
    else localStorage.removeItem(CHAT_STORAGE_KEY);
  } catch {
    // Private mode, a full quota, or storage blocked outright. Losing the scrollback on
    // reload is the old behaviour, not a failure worth surfacing mid-conversation.
  }
}

export function clearChat(): void {
  try {
    localStorage.removeItem(CHAT_STORAGE_KEY);
  } catch {
    // As above.
  }
}
